import { useEffect, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PreviewLead } from '@arcnet/contracts';
import { useLeadResult, useMyCampaigns, useNextLead } from '../../lib/api/hooks-dialler.js';
import { api } from '../../lib/api/client.js';
import { useSession } from '../../lib/auth/session.js';
import { relativeTime } from '../../lib/format.js';
import { Badge, ErrorBanner } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import type { BrowserPhoneApi, CallEnd } from './useBrowserPhone.js';

/**
 * Preview campaigns in the workspace (docs/36 D1b): take the next lead, see who they are and why
 * they are being called, call them from this phone, and say how it went. The outcome moves the
 * lead on by the campaign's own rules -- back later, done, a booked callback, or do-not-call.
 *
 * The lead stays on the screen until the agent says how the call went: a reloaded page gives the
 * same lead back (the API keeps it for the agent), so nobody is lost between two calls.
 */

const WHY: Record<PreviewLead['why'], { label: string; tone: 'info' | 'warn' | 'ok' | 'muted' }> = {
  callback: { label: 'Callback due', tone: 'warn' },
  yours: { label: 'Your lead', tone: 'ok' },
  retry: { label: 'Trying again', tone: 'muted' },
  fresh: { label: 'New', tone: 'info' },
};

/** What the network said, as an outcome. The agent can change it. */
function outcomeOf(end: CallEnd | null): string {
  if (!end) return 'no_answer';
  if (end.answered) return 'answered';
  const c = (end.cause ?? '').toLowerCase();
  if (c.includes('busy')) return 'busy';
  if (c.includes('no answer') || c.includes('timeout') || c.includes('unavailable') || c.includes('canceled')) return 'no_answer';
  return 'failed';
}

export function CampaignPanel({ tenantId, extensionId, phone, onActive, onDone }: {
  tenantId: string; extensionId: string; phone: BrowserPhoneApi;
  /** Whether a lead is on the screen: the workspace's own wrap-up steps aside for it. */
  onActive: (active: boolean) => void;
  onDone: () => void;
}) {
  const can = useSession((s) => s.can);
  const mine = useMyCampaigns(tenantId, extensionId, can('dialler:agent'));
  const next = useNextLead(tenantId, extensionId);
  const campaigns = mine.data?.data ?? [];
  const [campaignId, setCampaignId] = useState<string>('');
  const [lead, setLead] = useState<PreviewLead | null>(null);
  const [none, setNone] = useState<string | null>(null);

  useEffect(() => {
    if (!campaignId && campaigns.length) setCampaignId(campaigns[0]!.id);
  }, [campaigns, campaignId]);
  // A lead left open (a reload, another tab): take it straight back.
  const open = campaigns.find((c) => c.open_attempt_id && !lead);
  useEffect(() => {
    if (open && !next.isPending) {
      next.mutate(open.id, { onSuccess: (r) => { if ('attempt_id' in r) { setCampaignId(open.id); setLead(r); } } });
    }
  }, [open?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { onActive(lead !== null); }, [lead, onActive]);

  if (!can('dialler:agent') || campaigns.length === 0) return null;
  const current = campaigns.find((c) => c.id === campaignId);
  const callEnded = lead && phone.lastEnd?.attemptId === lead.attempt_id && !phone.call ? phone.lastEnd : null;
  const onThisCall = lead && phone.call !== null;

  return (
    <div className="arc-card arc-card-body">
      <div className="arc-row" style={{ gap: 8, marginBottom: 10 }}>
        <Icon name="callOut" size={15} />
        <strong style={{ fontSize: 13 }}>Campaign</strong>
        <div className="arc-spacer" />
        {campaigns.length > 1 && !lead ? (
          <select className="arc-input" style={{ maxWidth: 220 }} aria-label="Campaign" value={campaignId}
            onChange={(e) => { setCampaignId(e.target.value); setNone(null); }}>
            {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        ) : <span className="arc-muted" style={{ fontSize: 12.5 }}>{lead?.campaign.name ?? current?.name}</span>}
      </div>

      {!lead && (
        <>
          <div className="arc-row" style={{ gap: 10 }}>
            <button type="button" className="arc-btn arc-btn-primary" disabled={!campaignId || next.isPending || phone.call !== null}
              onClick={() => { setNone(null); next.mutate(campaignId, { onSuccess: (r) => { if ('attempt_id' in r) setLead(r); else setNone(r.none); } }); }}>
              Next lead
            </button>
            {current && (
              <span className="arc-muted" style={{ fontSize: 12.5 }}>
                {current.due_now} ready{current.callbacks_due ? `, ${current.callbacks_due} callback${current.callbacks_due === 1 ? '' : 's'} due` : ''}
              </span>
            )}
          </div>
          {none && <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 8 }}>{none}</p>}
          {next.error && <ErrorBanner error={next.error} />}
        </>
      )}

      {lead && (
        <>
          <LeadCard lead={lead} />
          {!callEnded && (
            <div className="arc-row" style={{ gap: 8, marginTop: 12 }}>
              <button type="button" className="arc-btn arc-btn-primary" disabled={phone.state !== 'ready' || phone.call !== null}
                onClick={() => phone.dial(`+${lead.phone}`, { attemptId: lead.attempt_id })}>
                <Icon name="phone" size={13} /> Call +{lead.phone}
              </button>
              {!onThisCall && (
                <SkipButton tenantId={tenantId} extensionId={extensionId} attemptId={lead.attempt_id}
                  onDone={() => { setLead(null); onDone(); }} />
              )}
              {phone.state !== 'ready' && <span className="arc-muted" style={{ fontSize: 12.5 }}>Switch the phone on to call.</span>}
            </div>
          )}
          {callEnded && (
            <Outcome tenantId={tenantId} extensionId={extensionId} lead={lead} end={callEnded}
              onDone={() => { setLead(null); onDone(); void mine.refetch(); }} />
          )}
        </>
      )}
    </div>
  );
}

function LeadCard({ lead }: { lead: PreviewLead }) {
  const [history, setHistory] = useState(false);
  const who = [lead.first_name, lead.last_name].filter(Boolean).join(' ') || 'No name';
  const shown = lead.fields.filter((f) => f.value !== null && f.value !== '');
  return (
    <div>
      <div className="arc-row" style={{ gap: 8, alignItems: 'baseline' }}>
        <strong style={{ fontSize: 16 }}>{who}</strong>
        <Badge tone={WHY[lead.why].tone}>{WHY[lead.why].label}</Badge>
        {lead.attempts > 0 && <span className="arc-subtle" style={{ fontSize: 12 }}>called {lead.attempts} time{lead.attempts === 1 ? '' : 's'} before</span>}
      </div>
      <div className="arc-mono" style={{ fontSize: 13, marginTop: 2 }}>+{lead.phone}{lead.email && <span className="arc-subtle"> · {lead.email}</span>}</div>
      {lead.callback_note && <div className="arc-banner arc-banner-info" style={{ marginTop: 8 }}><Icon name="clock" size={13} /><span>{lead.callback_note}</span></div>}
      {shown.length > 0 && (
        <dl className="arc-kv" style={{ marginTop: 10 }}>
          {shown.map((f) => (
            // Text, never markup: these values came from the client's spreadsheet (docs/36 §8.3).
            <div key={f.key} style={{ display: 'contents' }}><dt>{f.label}</dt><dd>{typeof f.value === 'boolean' ? (f.value ? 'Yes' : 'No') : String(f.value)}</dd></div>
          ))}
        </dl>
      )}
      {lead.history.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={() => setHistory((v) => !v)}>
            {history ? 'Hide' : 'Show'} earlier calls ({lead.history.length})
          </button>
          {history && (
            <ul style={{ fontSize: 12.5, margin: '6px 0 0', paddingLeft: 18 }}>
              {lead.history.map((h, i) => (
                <li key={i}>{relativeTime(h.at)}{h.agent ? `, ${h.agent}` : ''}: {h.disposition ?? h.outcome ?? '—'}{h.note ? ` — ${h.note}` : ''}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function SkipButton({ tenantId, extensionId, attemptId, onDone }: { tenantId: string; extensionId: string; attemptId: string; onDone: () => void }) {
  const result = useLeadResult(tenantId, extensionId);
  return (
    <button type="button" className="arc-btn" disabled={result.isPending}
      onClick={() => result.mutate({ attemptId, outcome: 'cancelled' }, { onSuccess: onDone })}>Skip</button>
  );
}

const OUTCOMES: ReadonlyArray<readonly [string, string]> = [
  ['answered', 'Spoke to them'], ['no_answer', 'No answer'], ['busy', 'Busy'], ['machine', 'Answering machine'], ['failed', 'Could not connect'],
];

function Outcome({ tenantId, extensionId, lead, end, onDone }: {
  tenantId: string; extensionId: string; lead: PreviewLead; end: CallEnd; onDone: () => void;
}) {
  const result = useLeadResult(tenantId, extensionId);
  const codes = useQuery({
    queryKey: ['disposition-codes', tenantId],
    queryFn: () => api.get<{ data: Array<{ code: string; label: string; is_active: boolean; requires_note: boolean }> }>(
      `/v1/tenants/${tenantId}/disposition-codes`),
  });
  const [outcome, setOutcome] = useState(outcomeOf(end));
  const [disposition, setDisposition] = useState('');
  const [note, setNote] = useState('');
  const [callbackAt, setCallbackAt] = useState('');
  const [mine, setMine] = useState(true);
  const [dnc, setDnc] = useState(false);
  const active = (codes.data?.data ?? []).filter((c) => c.is_active);
  const needsNote = active.find((c) => c.code === disposition)?.requires_note ?? false;
  const needsDisposition = outcome === 'answered' && !disposition;

  return (
    <form style={{ marginTop: 12, borderTop: '1px solid var(--arc-border)', paddingTop: 12 }} onSubmit={(e: FormEvent) => {
      e.preventDefault();
      result.mutate({
        attemptId: lead.attempt_id, outcome, disposition: disposition || null, note: note.trim() || null,
        callback_at: callbackAt ? new Date(callbackAt).toISOString() : null, callback_mine: mine, do_not_call: dnc,
      }, { onSuccess: onDone });
    }}>
      <strong style={{ fontSize: 13 }}>How did it go?</strong>
      <div className="arc-row" style={{ gap: 6, flexWrap: 'wrap', margin: '8px 0' }}>
        {OUTCOMES.map(([k, label]) => (
          <button key={k} type="button" className={`arc-btn arc-btn-sm ${outcome === k ? 'arc-btn-primary' : ''}`} onClick={() => setOutcome(k)}>{label}</button>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="cr-disp">Disposition{outcome === 'answered' ? '' : ' (optional)'}</label>
          <select id="cr-disp" className="arc-input" value={disposition} onChange={(e) => setDisposition(e.target.value)}>
            <option value="">Choose…</option>
            {active.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="cr-cb">Call back (optional)</label>
          <input id="cr-cb" className="arc-input" type="datetime-local" value={callbackAt} onChange={(e) => setCallbackAt(e.target.value)} />
          {callbackAt && (
            <label className="arc-row" style={{ gap: 6, fontSize: 12.5, marginTop: 4 }}>
              <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} /> Only me
            </label>
          )}
        </div>
      </div>
      <div className="arc-field" style={{ margin: '10px 0 0' }}>
        <label className="arc-label" htmlFor="cr-note">Note{needsNote ? '' : ' (optional)'}</label>
        <textarea id="cr-note" className="arc-input" rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      <label className="arc-row" style={{ gap: 6, fontSize: 12.5, marginTop: 8 }}>
        <input type="checkbox" checked={dnc} onChange={(e) => setDnc(e.target.checked)} /> They asked not to be called again
      </label>
      <div className="arc-row" style={{ gap: 10, marginTop: 12 }}>
        <button type="submit" className="arc-btn arc-btn-primary"
          disabled={result.isPending || needsDisposition || (needsNote && !note.trim())}>Save</button>
        {needsDisposition && <span className="arc-muted" style={{ fontSize: 12.5 }}>Choose what the call was.</span>}
      </div>
      {result.error && <ErrorBanner error={result.error} />}
    </form>
  );
}
